import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PACKAGE_INTERESTS } from '../data/packages';
import { useCatalog, INT_KEYS } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PublicPartnerProducts } from '../components/PublicPartnerProducts';
import { MarketplaceCard } from '../components/MarketplaceCard';

const DEST_GROUPS = ['Todos', 'São Tomé', 'Príncipe', 'São Tomé + Príncipe', 'Santiago'];
const NEW_DESTS = [];
const DURATIONS = [
  { id: 'Todos', key: null },
  { id: '3 dias', key: 'dur.3' },
  { id: '5 dias', key: 'dur.5' },
  { id: '7+ dias', key: 'dur.7p' },
];
const INTERESTS = ['Todos', 'Natureza', 'Praia', 'Cultura', 'Gastronomia', 'Caminhada', 'Mar', 'Aventura', 'Relaxamento'];

const durBucket = (d) => {
  const n = parseInt(d);
  if (n <= 3) return '3 dias';
  if (n === 5) return '5 dias';
  return '7+ dias';
};

const destMatch = (p, g) =>
  g === 'Santiago' ? p.destination.includes('Santiago') : p.destination === g;

export default function PackagesPage() {
  const { t, lang } = useLang();
  const { packages } = useCatalog();
  useSEO('Pacotes Completos | Eco Collection', 'Viagens completas em São Tomé, Príncipe e Santiago, Cabo Verde — preço sob consulta.');
  const [dest, setDest] = useState('Todos');
  const [duration, setDuration] = useState('Todos');
  const [style, setStyle] = useState('Todos');
  const [interest, setInterest] = useState('Todos');
  const STYLES = ['Todos', ...new Set(packages.map((p) => p.style))];

  const visible = packages.filter((p) => {
    if (dest !== 'Todos' && !destMatch(p, dest)) return false;
    if (duration !== 'Todos' && durBucket(p.duration) !== duration) return false;
    if (style !== 'Todos' && p.style !== style) return false;
    if (interest !== 'Todos' && !(PACKAGE_INTERESTS[p.slug] || []).includes(interest)) return false;
    return true;
  });

  const selectCls = 'rounded-full border border-eco-ink/15 bg-white px-4 py-2 text-sm text-eco-ink/70 outline-none focus:border-eco-gold';

  return (
    <div data-testid="packages-page">
      <PageHeader kicker={t('page.packages.k')} title={t('page.packages.t')} sub={t('page.packages.s')} />
      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="flex flex-wrap items-center justify-center gap-3">
            <select data-testid="packages-filter-destination" value={dest} onChange={(e) => setDest(e.target.value)} className={selectCls}>
              {DEST_GROUPS.map((g) => <option key={g} value={g}>{g === 'Todos' ? `${t('filter.destination')}: ${t('filter.all')}` : g === 'Santiago' ? 'Santiago, Cabo Verde' : g}</option>)}
            </select>
            <select data-testid="packages-filter-duration" value={duration} onChange={(e) => setDuration(e.target.value)} className={selectCls}>
              {DURATIONS.map((d) => <option key={d.id} value={d.id}>{d.id === 'Todos' ? `${t('filter.duration')}: ${t('filter.all')}` : t(d.key)}</option>)}
            </select>
            <select data-testid="packages-filter-style" value={style} onChange={(e) => setStyle(e.target.value)} className={selectCls}>
              {STYLES.map((s) => <option key={s} value={s}>{s === 'Todos' ? `${t('filter.style')}: ${t('filter.all')}` : s}</option>)}
            </select>
            <select data-testid="packages-filter-interest" value={interest} onChange={(e) => setInterest(e.target.value)} className={selectCls}>
              {INTERESTS.map((i) => <option key={i} value={i}>{i === 'Todos' ? `${t('wiz.interests')}: ${t('filter.all')}` : t(INT_KEYS[i] || i)}</option>)}
            </select>
            <span data-testid="packages-count" className="text-sm text-eco-ink/50">{visible.length}</span>
          </Reveal>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <MarketplaceCard
                  to={`/full-packages/${p.slug}`}
                  testid={`package-card-${p.slug}`}
                  image={p.img}
                  imageBadge={p.destination}
                  eyebrow={`${p.destination} · ${p.style}`}
                  title={p.name}
                  location={p.idealFor}
                  duration={p.duration}
                  partner={t('partners.kicker')}
                  priceLabel={p.base_price ? t('price.fromPerson', { price: new Intl.NumberFormat(lang, { style: 'currency', currency: p.base_price_currency || 'EUR', maximumFractionDigits: 0 }).format(p.base_price) }) : undefined}
                  cta={t('cta.proposal')}
                />
              </Reveal>
            ))}
          </div>
          {visible.length === 0 && (
            <div data-testid="packages-empty" className="mt-10 rounded-3xl border border-dashed border-eco-ink/15 bg-white py-14 text-center">
              <p className="font-serif text-xl text-eco-ink/70">{dest === 'Todos' ? t('empty.all') : t('empty.dest', { dest })}</p>
              {NEW_DESTS.includes(dest) && (
                <Link to={`/plan-my-trip?destination=${encodeURIComponent(dest)}`} data-testid="packages-empty-cta" className="group mt-6 inline-flex items-center gap-2 rounded-full bg-eco-green px-7 py-3 text-sm font-semibold text-white hover:bg-eco-deep">
                  {t('nav.plan')} <ArrowRight size={15} />
                </Link>
              )}
            </div>
          )}
          <PublicPartnerProducts type="package" island={dest} />
        </div>
      </section>
    </div>
  );
}
